"use client";

import { useAuth } from "@/lib/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Coins, Key, Activity, Zap, MessageSquare, Sparkles, Share2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useTranslations } from "next-intl";
import { ProgressTracker } from "@/components/dashboard/ProgressTracker";
import { QuickStartCard } from "@/components/dashboard/QuickStartCard";
import { LearningAccordion } from "@/components/dashboard/LearningAccordion";

export default function DashboardPage() {
  const { user } = useAuth();
  const [keyCount, setKeyCount] = useState(0);
  const t = useTranslations("dashboard");

  useEffect(() => {
    api.getKeys().then(async (res) => {
      if (res.ok) {
        const keys = await res.json();
        setKeyCount(keys.length);
      }
    });
  }, []);

  if (!user) return null;

  // Format token balance with thousand separators
  const formatToken = (balance: number) => {
    let formatted;
    if (balance === 0) formatted = "0.000000";
    else if (balance < 0.01) formatted = balance.toFixed(6);
    else if (balance < 1) formatted = balance.toFixed(4);
    else formatted = balance.toFixed(2);

    // Add thousand separators
    const [whole, decimal] = formatted.split(".");
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimal ? `${withCommas}.${decimal}` : withCommas;
  };

  // Progress tracker steps
  const progressSteps = [
    {
      id: "account",
      title: t("progress.step1.title"),
      description: t("progress.step1.desc"),
      status: "done" as const,
      icon: <Zap className="w-5 h-5" />,
    },
    {
      id: "api-key",
      title: t("progress.step2.title"),
      description: t("progress.step2.desc"),
      status: keyCount > 0 ? ("done" as const) : ("current" as const),
      icon: <Key className="w-5 h-5" />,
      ctaText: t("progress.step2.cta"),
      ctaHref: "/dashboard/keys",
    },
    {
      id: "chat",
      title: t("progress.step3.title"),
      description: t("progress.step3.desc"),
      status: keyCount > 0 ? ("current" as const) : ("pending" as const),
      icon: <MessageSquare className="w-5 h-5" />,
      ctaText: t("progress.step3.cta"),
      ctaHref: "/chat",
    },
    {
      id: "content",
      title: t("progress.step4.title"),
      description: t("progress.step4.desc"),
      status: ("pending" as const),
      icon: <Share2 className="w-5 h-5" />,
      ctaText: t("progress.step4.cta"),
      ctaHref: "/dashboard/keys", // placeholder, will update later
    },
  ];

  // Learning accordion items
  const learningItems = [
    {
      question: t("learn.q1"),
      answer: t("learn.a1"),
      learnMoreText: t("learn.learn1"),
      learnMoreHref: "/docs/api-keys",
    },
    {
      question: t("learn.q2"),
      answer: t("learn.a2"),
      learnMoreText: t("learn.learn2"),
      learnMoreHref: "/docs/chat",
    },
    {
      question: t("learn.q3"),
      answer: t("learn.a3"),
      learnMoreText: t("learn.learn3"),
      learnMoreHref: "/docs/earning",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          {t("welcome", { name: user.name })}
        </h1>
        <p className="text-gray-600 mt-2 text-lg">{t("subtitle")}</p>
      </div>

      {/* Stats Cards (Mini) */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium text-gray-700">{t("cards.credit")}</CardTitle>
            <Coins className="w-5 h-5 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatToken(user.tokenBalance)}</div>
            <div className="text-xs text-gray-500 mt-1">USD</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium text-gray-700">{t("cards.apiKeys")}</CardTitle>
            <Key className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{keyCount}</div>
            <div className="text-xs text-gray-500 mt-1">Keys Created</div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium text-gray-700">{t("cards.tier")}</CardTitle>
            <Zap className="w-5 h-5 text-amber-600" />
          </CardHeader>
          <CardContent>
            <Badge variant="secondary" className="text-sm">{user.tier.toUpperCase()}</Badge>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-sm font-medium text-gray-700">{t("cards.status")}</CardTitle>
            <Activity className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <Badge variant="default" className="bg-green-100 text-green-700 hover:bg-green-100">
              {t("cards.active")}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Progress Tracker */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          {t("progress.title")}
        </h2>
        <ProgressTracker steps={progressSteps} />
      </div>

      {/* Quick Start Cards */}
      <div>
        <h2 className="text-xl font-bold mb-4">{t("quickstart.title")}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickStartCard
            icon="🔑"
            title={t("quickstart.cards.keys.title")}
            description={t("quickstart.cards.keys.desc")}
            ctaText={t("quickstart.cards.keys.cta")}
            ctaHref="/dashboard/keys"
            variant={keyCount === 0 ? "highlight" : "default"}
          />
          <QuickStartCard
            icon="💬"
            title={t("quickstart.cards.chat.title")}
            description={t("quickstart.cards.chat.desc")}
            ctaText={t("quickstart.cards.chat.cta")}
            ctaHref="/chat"
            variant={keyCount > 0 ? "highlight" : "default"}
          />
          <QuickStartCard
            icon="📱"
            title={t("quickstart.cards.content.title")}
            description={t("quickstart.cards.content.desc")}
            ctaText={t("quickstart.cards.content.cta")}
            ctaHref="/dashboard"
            variant="soon"
          />
          <QuickStartCard
            icon="📊"
            title={t("quickstart.cards.analytics.title")}
            description={t("quickstart.cards.analytics.desc")}
            ctaText={t("quickstart.cards.analytics.cta")}
            ctaHref="/dashboard"
            variant="soon"
          />
        </div>
      </div>

      {/* Learning Resources */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200 p-6">
        <LearningAccordion items={learningItems} title={t("learn.title")} />
      </div>
    </div>
  );
}
